import { html, css, type TemplateResult } from '@design.estate/dees-element';
import { demoPage } from './templates.js';
import { previewArtwork, makePreviewAudio } from './media.samples.js';
import { previewVideo } from './media.video.js';
import '../elements/00group-media/dees-preview/dees-preview.js';
import { DeesModal } from '../elements/00group-overlay/dees-modal/dees-modal.js';

const styles = css`
  .mixed-grid { display: grid; grid-template-columns: repeat(3, 150px); gap: 24px; justify-content: start; padding: 16px 0 24px; }
  @media (max-width: 600px) { .mixed-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px 12px; } }
  .demo-container { color: var(--dees-color-text-primary); }
  .demo-section { margin-bottom: 32px; }
  .demo-section h3 { font-size: 13px; font-weight: 600; margin: 0 0 16px; }
  .tile-row, .preview-row { display: flex; gap: 20px; flex-wrap: wrap; align-items: flex-start; margin-bottom: 16px; }
  .preview-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 20px; }
  .preview-label { width: 100%; font-size: 12px; color: var(--dees-color-text-secondary); }
`;

const openThumbnail = async (event: CustomEvent) => {
  const source = event.composedPath().find(node => node instanceof HTMLElement && node.localName.startsWith('dees-thumbnail-')) as HTMLElement;
  if (!source) return;
  const detail = event.detail;
  const kind = source.localName.replace('dees-thumbnail-', '');
  let content: TemplateResult;
  if (kind === 'folder') content = html`<ul>${detail.items.map((item: { name: string }) => html`<li>${item.name}</li>`)}</ul>`;
  else if (kind === 'note') content = html`<pre style="white-space:pre-wrap;font:13px/1.6 var(--dees-font-family-mono)">${detail.content}</pre>`;
  else content = html`<dees-preview style="display:block;height:460px" .url=${detail.pdfUrl || detail.src} .filename=${(source as any).label} .frameless=${true}></dees-preview>`;
  await DeesModal.createAndShow({ heading: (source as any).label || detail.title || detail.name || 'Preview', width: 'large', content, menuOptions: [] });
};

const artwork = `data:image/svg+xml,${encodeURIComponent(previewArtwork)}`;
let audioSample: string | undefined;
const audio = () => audioSample ??= `data:audio/wav;base64,${btoa(Array.from(makePreviewAudio(), byte => String.fromCharCode(byte)).join(''))}`;

export const thumbnailDemo = (title: string, description: string, content: TemplateResult) => demoPage(title, description, html`
  <style>${styles}</style>
  <div @tile-click=${openThumbnail}>
    <div class="mixed-grid" aria-label="Studio files">
      <dees-thumbnail-image size="small" fit="contain" .src=${artwork} alt="Illustrated mountain landscape" label="Perspective.svg"></dees-thumbnail-image>
      <dees-thumbnail-pdf size="small" pdfUrl="https://raw.githubusercontent.com/mozilla/pdf.js/ba2edeae/web/compressed.tracemonkey-pldi-09.pdf" label="Research paper.pdf"></dees-thumbnail-pdf>
      <dees-thumbnail-video size="small" fit="contain" .src=${`data:video/webm;base64,${previewVideo}`} label="Studio motion.webm"></dees-thumbnail-video>
      <dees-thumbnail-audio size="small" .src=${audio()} .title=${'A small arrival'} label="A small arrival.wav"></dees-thumbnail-audio>
      <dees-thumbnail-note size="small" .title=${'Ideas for today'} content="Make room for the work.

Keep the details close.

Leave a little space for what comes next." label="Studio notes.txt"></dees-thumbnail-note>
      <dees-thumbnail-folder size="small" name="References" label="References" .items=${[{type:'image',name:'Perspective.svg',thumbnailSrc:artwork},{type:'pdf',name:'Research paper.pdf'},{type:'note',name:'Studio notes.txt'}]}></dees-thumbnail-folder>
    </div>
    <p class="dees-demo-caption">Open a tile with a click, Enter, or Space. Hover a document to browse its pages.</p>
    <details class="dees-demo-details"><summary>Explore ${title.toLowerCase()} · samples and sizes</summary>${content}</details>
  </div>
`);
